/**
 * M2 audio routing (M2 plan §2.12; audio-direction §2, §3 M2 rows, §4.3): space events and tool readouts → the
 * engine's one-shots and loops, the score's stingers and station radio, and comms lines. Pure bookkeeping on the
 * router's preallocated state; the feature (src/features/m2audio) owns the engine, music and comms handles.
 *
 * - One-shots: the player's own tools, hold and docking clamps go through the hull (`ROUTE.hull`), ship-computer
 *   tones through the cockpit (`ROUTE.ui`), world events (chips, cracks, bursts, cuts, vents) are positional.
 * - Loops (from the readouts, every frame): the mining beam (rate follows laser power, louder on contact), the
 *   fracture strain (level and rate follow instability), the cutting beam (harsher while spilling) and the
 *   tractor hum (deeper while holding a part). All off when the player is dead or docked.
 * - Music: `fractureSuccess` (the player's) → the fracture stinger; `SEV.career ownership` → the ownership stinger;
 *   a defeated raider or repo group → wave cleared. The docking phase → the desired station radio, applied
 *   whenever a music system with `setStationRadio` is present (so a career that starts docked gets its radio once
 *   the lazily loaded score arrives).
 * - Comms: clearance notices speak as the station (`stations.json commsSeed`), lease notices as Tethys Crew
 *   Finance (a fixed seed), queued here and spoken by the feature.
 *
 * `routeSpaceEvents`, `routeReadouts` (with `syncBoomAlarm`) and `applyMusicCues` allocate nothing (D-014; source-scanned in the tests).
 */
import type { DockingReadout, MiningReadout, NavKind, SalvageReadout, ScanReadout } from '../../space/contracts'
import { CAREER_NOTICE, ENCOUNTER_KIND, firstReadableSpace, SEV, SEV_MASK, type SpaceEventRing } from '../../space/events'
import { LOOP, ROUTE, type LoopId, type Route } from '../engine'
import type { StingerKind } from '../music/conductor'
import type { SoundSink } from '../router'
import { soundIndex, type SfxConfig, type SoundId } from '../sfxSchema'
import { PRIORITY, type Priority } from '../voices'

/** The engine surface the M2 router drives (the engine, or a recorder in tests). */
export interface M2Sink extends SoundSink {
  play(sound: number, gain: number, route: Route, priority: Priority, pan?: number, rate?: number, when?: number): number
  setLoop(loop: LoopId, on: boolean, gain: number, rate: number, cutoff: number, when?: number): void
}

/** The music surface the router drives (the score's `MusicSystem`, or a recorder). */
export interface M2MusicSink {
  stinger(kind: StingerKind): void
  setStationRadio?(on: boolean): void
}

/** The readouts the loops follow (the space runtimes' own objects, read in place). */
export interface M2Readouts {
  readonly mining: Readonly<MiningReadout>
  readonly salvage: Readonly<SalvageReadout>
  readonly scan: Readonly<ScanReadout>
  readonly docking: Readonly<DockingReadout>
}

/** Comms lines queued per frame (more in one frame are dropped: nobody talks over four lines). */
export const COMMS_QUEUE = 4

export interface M2AudioRouter {
  readonly config: SfxConfig
  /** Sound index per M2 sound. */
  readonly s: Readonly<Record<SoundId, number>>
  /** Voice seed per station index (`stations.json commsSeed`). */
  readonly stationSeeds: Int32Array
  /** Space ring cursor. */
  cursor: number
  /** Stingers asked for since the last `applyMusicCues`. */
  fracture: boolean
  ownership: boolean
  groupCleared: boolean
  /** The station radio the docking phase asks for (`routeReadouts`), what was applied (0 off, 1 on), and to which music. */
  radioWanted: boolean
  radioApplied: number
  radioMusic: M2MusicSink | null
  /** Comms lines to speak: seed and stress per entry. */
  readonly commsSeed: Int32Array
  readonly commsStress: Float32Array
  commsCount: number
  /** Scan progress last frame (the sweep plays when a scan starts). */
  lastScan: number
  lastScanKind: NavKind
  lastScanIndex: number
  /**
   * Review B2 (P5a12c): one combined boom alarm. Bit k = the player's boom k is overheated (`SEV.overheat` / `SEV.cooled`
   * carry the boom in `a`). The overheat tone sounds when the first bit sets; `boomsCooled` when the last clears, only if
   * a boom past boom 0 overheated in that spell (`boomSpell`), so a single beam sounds as before.
   */
  boomMask: number
  boomSpell: number
}

/** Builds the router. `stationSeeds[i]` is station `i`'s comms voice seed. */
export function createM2AudioRouter(config: SfxConfig, stationSeeds: readonly number[]): M2AudioRouter {
  const s = {} as Record<SoundId, number>
  const ids: readonly SoundId[] = [
    'miningBeamLoop',
    'miningStrainLoop',
    'chipBreak',
    'rockCrack',
    'rockBurst',
    'overheat',
    'cuttingBeamLoop',
    'anchorCut',
    'coolantVent',
    'tractorLoop',
    'partSecured',
    'chunkCollect',
    'scanSweep',
    'scanDone',
    'clearanceGranted',
    'clearanceExpired',
    'dockClamp',
    'undockRelease',
    'holdFull',
    config.m2.tetherstone.sound,
    config.m2.partFreed.sound,
    config.m2.contractCompleted.sound,
    config.m2.groupArrived.sound,
    config.m2.moorGranted.sound,
    config.m2.boomsCooled.sound,
  ]
  for (const id of ids) {
    const i = soundIndex(id)
    if (i < 0) throw new Error(`M2 audio: sound "${id}" is not in the sound bank (src/audio/sfx.json)`)
    s[id] = i
  }
  return {
    config,
    s,
    stationSeeds: Int32Array.from(stationSeeds),
    cursor: 0,
    fracture: false,
    ownership: false,
    groupCleared: false,
    radioWanted: false,
    radioApplied: 0,
    radioMusic: null,
    commsSeed: new Int32Array(COMMS_QUEUE),
    commsStress: new Float32Array(COMMS_QUEUE),
    commsCount: 0,
    lastScan: 0,
    lastScanKind: 0,
    lastScanIndex: -1,
    boomMask: 0,
    boomSpell: 0,
  }
}

/**
 * B2 with B1: drops alarm bits the readout no longer shows (a load, a respawn, or booms 1–3 cooling silently off a
 * boom hull, which emit no `SEV.cooled`). Bits past boom 0 clear silently off a boom hull; on one, a mask that empties
 * here sounds `boomsCooled` as the event would (the event then finds the bit clear and stays quiet). Allocation-free.
 */
function syncBoomAlarm(r: M2AudioRouter, m: Readonly<MiningReadout>, sink: M2Sink): void {
  if (r.boomMask === 0) return
  const b = m.booms
  const n = b === undefined ? 0 : b.count
  if (n <= 1) {
    r.boomSpell &= 1
    r.boomMask &= m.overheated ? 1 : 0
    if (r.boomMask === 0) r.boomSpell = 0
    return
  }
  let live = m.overheated ? 1 : 0
  for (let k = 1; k < n && b !== undefined; k++) if (b.overheated[k] === 1) live |= 1 << k
  r.boomMask &= live
  if (r.boomMask !== 0) return
  const m2 = r.config.m2
  if ((r.boomSpell & ~1) !== 0) sink.play(r.s[m2.boomsCooled.sound], m2.boomsCooled.gain, ROUTE.ui, PRIORITY.alarm, 0, m2.boomsCooled.rate)
  r.boomSpell = 0
}

/** Moves the cursor to the ring head without playing anything (engine not up yet, paused). */
export function skipSpaceEvents(r: M2AudioRouter, ring: SpaceEventRing): void {
  r.cursor = ring.head
}

function say(r: M2AudioRouter, seed: number, stress: number): void {
  if (r.commsCount >= COMMS_QUEUE) return
  r.commsSeed[r.commsCount] = seed
  r.commsStress[r.commsCount] = stress
  r.commsCount++
}

function stationSeed(r: M2AudioRouter, station: number): number {
  return station >= 0 && station < r.stationSeeds.length ? r.stationSeeds[station] : r.config.m2.comms.financeSeed
}

/**
 * Plays every space event since the last call. `player` is the player's eid (-1 when there is none): events of
 * other ships are positional only, and cockpit tones, stingers and comms follow the player's events alone.
 */
export function routeSpaceEvents(r: M2AudioRouter, ring: SpaceEventRing, player: number, sink: M2Sink): void {
  const s = r.s
  const m2 = r.config.m2
  const c = m2.comms
  for (let i = firstReadableSpace(ring, r.cursor); i < ring.head; i++) {
    const k = i & SEV_MASK
    const type = ring.type[k]
    const own = player >= 0 && ring.eid[k] === player
    const x = ring.x[k]
    const y = ring.y[k]
    const z = ring.z[k]
    switch (type) {
      case SEV.scanComplete:
        if (own) sink.play(s.scanDone, 1, ROUTE.ui, PRIORITY.alarm)
        break
      case SEV.chipBreak:
        sink.playWorld(s.chipBreak, 1, x, y, z, PRIORITY.near)
        break
      case SEV.oreCollected:
        if (own) sink.play(s.chunkCollect, 1, ROUTE.hull, PRIORITY.near)
        else sink.playWorld(s.chunkCollect, 1, x, y, z, PRIORITY.near)
        break
      case SEV.holdFull:
        if (own) sink.play(s.holdFull, 1, ROUTE.ui, PRIORITY.alarm)
        break
      case SEV.tetherstone:
        if (own) sink.play(s[m2.tetherstone.sound], m2.tetherstone.gain, ROUTE.ui, PRIORITY.alarm, 0, m2.tetherstone.rate)
        break
      case SEV.fractureSuccess:
        sink.playWorld(s.rockCrack, 1, x, y, z, PRIORITY.near)
        if (own) r.fracture = true
        break
      case SEV.fractureBurst:
        sink.playWorld(s.rockBurst, 1, x, y, z, own ? PRIORITY.incoming : PRIORITY.near)
        break
      case SEV.overheat:
        if (own) {
          const bit = 1 << (ring.a[k] & 3)
          if (r.boomMask === 0) sink.play(s.overheat, 1, ROUTE.ui, PRIORITY.alarm)
          r.boomMask |= bit
          r.boomSpell |= bit
        }
        break
      case SEV.cooled:
        if (own) {
          const was = r.boomMask
          r.boomMask &= ~(1 << (ring.a[k] & 3))
          if (was !== 0 && r.boomMask === 0) {
            if ((r.boomSpell & ~1) !== 0) sink.play(s[m2.boomsCooled.sound], m2.boomsCooled.gain, ROUTE.ui, PRIORITY.alarm, 0, m2.boomsCooled.rate)
            r.boomSpell = 0
          }
        }
        break
      case SEV.anchorCut:
        sink.playWorld(s.anchorCut, 1, x, y, z, PRIORITY.near)
        break
      case SEV.coolantVent:
        sink.playWorld(s.coolantVent, 1, x, y, z, own ? PRIORITY.incoming : PRIORITY.near)
        break
      case SEV.partFreed:
        sink.playWorld(s[m2.partFreed.sound], m2.partFreed.gain, x, y, z, PRIORITY.near)
        break
      case SEV.partSecured:
        if (own) sink.play(s.partSecured, 1, ROUTE.hull, PRIORITY.near)
        else sink.playWorld(s.partSecured, 1, x, y, z, PRIORITY.near)
        break
      case SEV.partRejected:
        if (own) sink.play(s.holdFull, 1, ROUTE.ui, PRIORITY.alarm)
        break
      case SEV.clearanceGranted:
        // A moor grant (c = 1, M10 P1e2) has its own cue from the moorGranted that follows.
        if (own && ring.c[k] === 0) {
          sink.play(s.clearanceGranted, 1, ROUTE.ui, PRIORITY.alarm)
          say(r, stationSeed(r, ring.a[k]), c.clearanceGranted)
        }
        break
      case SEV.moorGranted:
        if (own) {
          sink.play(s[m2.moorGranted.sound], m2.moorGranted.gain, ROUTE.ui, PRIORITY.alarm, 0, m2.moorGranted.rate)
          say(r, stationSeed(r, ring.a[k]), c.clearanceGranted)
        }
        break
      case SEV.clearanceDenied:
        if (own) {
          sink.play(s.clearanceExpired, 1, ROUTE.ui, PRIORITY.alarm)
          say(r, stationSeed(r, ring.a[k]), c.clearanceDenied)
        }
        break
      case SEV.clearanceExpired:
        if (own) {
          sink.play(s.clearanceExpired, 1, ROUTE.ui, PRIORITY.alarm)
          say(r, stationSeed(r, ring.a[k]), c.clearanceExpired)
        }
        break
      case SEV.docked:
        // The station radio follows the docking phase (routeReadouts), not these edges.
        if (own) {
          sink.play(s.dockClamp, 1, ROUTE.hull, PRIORITY.near)
        }
        break
      case SEV.undocked:
        if (own) {
          sink.play(s.undockRelease, 1, ROUTE.hull, PRIORITY.near)
        }
        break
      case SEV.encounterSpawned:
        if (ring.b[k] !== ENCOUNTER_KIND.contract) sink.play(s[m2.groupArrived.sound], m2.groupArrived.gain, ROUTE.ui, PRIORITY.alarm, 0, m2.groupArrived.rate)
        break
      case SEV.groupDefeated:
        r.groupCleared = true
        break
      case SEV.career:
        routeNotice(r, ring.a[k], sink)
        break
      default:
        break
    }
  }
  r.cursor = ring.head
}

/** A career notice: the ownership stinger, lease comms from Crew Finance, a contract tone. */
function routeNotice(r: M2AudioRouter, code: number, sink: M2Sink): void {
  const m2 = r.config.m2
  const c = m2.comms
  switch (code) {
    case CAREER_NOTICE.ownership:
      r.ownership = true
      break
    case CAREER_NOTICE.leasePaid:
      say(r, c.financeSeed, c.leasePaid)
      break
    case CAREER_NOTICE.paymentMissed:
      say(r, c.financeSeed, c.paymentMissed)
      break
    case CAREER_NOTICE.collections:
      say(r, c.financeSeed, c.collections)
      break
    case CAREER_NOTICE.repoWarning:
      say(r, c.financeSeed, c.repoWarning)
      break
    case CAREER_NOTICE.contractCompleted:
      sink.play(r.s[m2.contractCompleted.sound], m2.contractCompleted.gain, ROUTE.ui, PRIORITY.alarm, 0, m2.contractCompleted.rate)
      break
    default:
      break
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

/**
 * The tool loops from the readouts, the scan sweep and the desired radio. `alive` = the player's ship is alive
 * (loops stop at death). Call every frame the engine runs.
 */
export function routeReadouts(r: M2AudioRouter, ro: M2Readouts, alive: boolean, sink: M2Sink): void {
  const m2 = r.config.m2
  const docked = ro.docking.phase === 'docked'
  r.radioWanted = docked
  const active = alive && !docked

  const m = ro.mining
  syncBoomAlarm(r, m, sink)
  const beam = active && m.beamOn
  const b = m2.miningBeam
  const power = clamp01(m.power)
  sink.setLoop(LOOP.miningBeam, beam, m.contact ? b.gainContact : b.gainIdle, b.rateMin + (b.rateMax - b.rateMin) * power, m.contact ? b.cutoffContact : b.cutoffIdle)
  const st = m2.miningStrain
  const inst = clamp01(m.fracture.instability)
  const strain = beam && m.fracture.active && inst > st.threshold
  sink.setLoop(LOOP.miningStrain, strain, inst * st.gain, st.rateMin + (st.rateMax - st.rateMin) * inst, b.cutoffContact)

  const sv = ro.salvage
  const cb = m2.cuttingBeam
  const spill = sv.spilling
  sink.setLoop(LOOP.cuttingBeam, active && sv.beamOn, spill ? cb.spillGain : cb.gain, spill ? cb.spillRate : cb.rate, spill ? cb.spillCutoff : cb.cutoff)
  const tr = m2.tractor
  const held = sv.tractorPart >= 0
  sink.setLoop(LOOP.tractor, active && sv.tractorOn, held ? tr.heldGain : tr.gain, held ? tr.heldRate : tr.rate, tr.cutoff)

  // The scan sweep: once when a scan starts (progress leaves 0, or the scan moves to another target).
  const sc = ro.scan
  const p = sc.progress
  const fresh = sc.kind !== r.lastScanKind || sc.index !== r.lastScanIndex
  if (active && p > 0 && !sc.complete && (r.lastScan <= 0 || fresh)) sink.play(r.s.scanSweep, 1, ROUTE.ui, PRIORITY.alarm)
  r.lastScan = active ? p : 0
  r.lastScanKind = sc.kind
  r.lastScanIndex = sc.index
}

/** Stops every M2 loop (feature off, paused, disposed). */
export function stopM2Loops(sink: Pick<M2Sink, 'setLoop'>): void {
  sink.setLoop(LOOP.miningBeam, false, 0, 1, 20000)
  sink.setLoop(LOOP.miningStrain, false, 0, 1, 20000)
  sink.setLoop(LOOP.cuttingBeam, false, 0, 1, 20000)
  sink.setLoop(LOOP.tractor, false, 0, 1, 20000)
}

/**
 * Sends the pending stingers and the desired radio to the score (null while it loads: stingers are dropped, the
 * radio is applied once it arrives). Clears the stinger flags.
 */
export function applyMusicCues(r: M2AudioRouter, music: M2MusicSink | null): void {
  if (music !== null) {
    if (r.fracture) music.stinger('fractureSuccess')
    if (r.groupCleared) music.stinger('waveCleared')
    if (r.ownership) music.stinger('ownership')
    if (music !== r.radioMusic) {
      // A fresh score starts with its radio off.
      r.radioMusic = music
      r.radioApplied = 0
    }
    const want = r.radioWanted ? 1 : 0
    if (want !== r.radioApplied && music.setStationRadio !== undefined) {
      music.setStationRadio(r.radioWanted)
      r.radioApplied = want
    }
  }
  r.fracture = false
  r.groupCleared = false
  r.ownership = false
}
